import { theme as defaultTheme } from "@mindlercare/ui-web";
import Paper from "@mui/material/Paper";
import MaterialTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { Component } from "react";
import { useDemo } from "contexts/DemoContext";
import TableHead from "./tableHead";

function desc(a, b, orderBy) {
  if (!a || !b) {
    return 0;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const styles = () => ({
  root: {
    width: "100%",
    boxShadow: "none",
    marginTop: defaultTheme.MARGIN.SM,
  },
  childrenWrapper: {
    padding: "8px 0",
  },
  table: {
    minWidth: 1020,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  tableCell: {
    paddingLeft: 0,
    paddingRight: 15,
    cursor: "pointer",
  },
});

class TableBackendFilter extends Component {
  state = {
    order: "asc",
    orderBy: "",
    selected: [],
    page: 0,
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = "desc";

    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }

    this.setState({ order, orderBy });
  };

  handleClick = (row) => {
    if (typeof this.props.onClick === "function") {
      this.props.onClick(row);
    }
  };

  getRowContent = (n, rowIndex) => {
    const { classes, sensitiveFields, demo } = this.props;

    const columnIds = this.props.tableColumns.reduce((acc, curr) => {
      return Object.assign(acc, { [curr.id]: 1 });
    }, {});

    return Object.keys(n)
      .filter((key) => key !== "id" && key in columnIds)
      .map((key, keyIndex) => {
        const value =
          demo && sensitiveFields && sensitiveFields.indexOf(key) !== -1
            ? "******"
            : n[key];
        if (typeof value === "object") {
          return (
            <TableCell
              classes={{ root: classes.tableCell }}
              style={{ color: "red" }}
              component={!keyIndex ? "th" : "td"}
              scope={!keyIndex ? "row" : null}
              key={`tableCell-${rowIndex}-${keyIndex}`}
              padding="normal"
            >
              {"Invalid value"}
            </TableCell>
          );
        }
        const tableCellContent = (
          <TableCell
            classes={{ root: classes.tableCell }}
            component={!keyIndex ? "th" : "td"}
            scope={!keyIndex ? "row" : null}
            key={`tableCell-${rowIndex}-${keyIndex}`}
            padding="normal"
          >
            {value}
          </TableCell>
        );
        return value === "CreditCard" ? (
          <Tooltip
            key={`toolTip-${rowIndex}-${keyIndex}`}
            title={n.provider}
            placement="top-start"
          >
            {tableCellContent}
          </Tooltip>
        ) : (
          tableCellContent
        );
      });
  };

  getValidRows = () => {
    const validRows = this.props.rows.filter(
      (row) => typeof row === "object" && row !== null,
    );
    return validRows;
  };

  getTableContent = () => {
    const { order, orderBy } = this.state;
    const { rowsPerPage } = this.props;
    const validRows = this.getValidRows();
    return stableSort(validRows, getSorting(order, orderBy))
      .slice(
        this.state.page * rowsPerPage,
        this.state.page * rowsPerPage + rowsPerPage,
      )
      .map((n, rowIndex) => {
        return (
          <TableRow
            hover
            onClick={() => this.handleClick(n)}
            tabIndex={-1}
            key={n.id}
          >
            {this.getRowContent(n, rowIndex)}
          </TableRow>
        );
      });
  };

  handleChangePage = (e, page) => {
    this.setState({
      page,
    });
    this.props.handleChangePage(page);
  };

  render() {
    const { classes, rowsPerPage, children } = this.props;
    const { order, orderBy } = this.state;
    return (
      <Paper className={classes.root}>
        {children && <div className={classes.childrenWrapper}>{children}</div>}
        <div className={classes.tableWrapper}>
          <MaterialTable className={classes.table} aria-labelledby="tableTitle">
            <TableHead
              rows={this.props.tableColumns}
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
            />
            <TableBody>{this.getTableContent()}</TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={this.getValidRows().length}
                  rowsPerPageOptions={[]}
                  rowsPerPage={rowsPerPage}
                  page={this.state.page}
                  backIconButtonProps={{
                    "aria-label": "Previous Page",
                  }}
                  nextIconButtonProps={{
                    "aria-label": "Next Page",
                  }}
                  onPageChange={this.handleChangePage}
                />
              </TableRow>
            </TableFooter>
          </MaterialTable>
        </div>
      </Paper>
    );
  }
}

TableBackendFilter.propTypes = {
  classes: PropTypes.object.isRequired,
  rows: PropTypes.array,
  tableColumns: PropTypes.array,
  filters: PropTypes.array,
  tableTitle: PropTypes.string,
  onClick: PropTypes.func,
  sensitiveFields: PropTypes.array,
  demo: PropTypes.bool,
  handleChangePage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  children: PropTypes.element,
};

const StyledTableBackendFilter = withStyles(styles)(TableBackendFilter);

const TableBackendFilterWithDemo = (props) => {
  const demo = useDemo();
  return <StyledTableBackendFilter {...props} demo={demo} />;
};

export default TableBackendFilterWithDemo;
